'use client';

import { Form, Formik } from 'formik';
import { Box, Stack, Typography } from '@mui/material';
import useInitialValues from '../prefill/useInitialValues';
import { DynamicFormType } from '../../type';
import RichText from '../../../../RichText';
import RecaptchaLinks from '../../../../../providers/Recaptcha/RecaptchaLinks';
import Optional from '../../../../Optional';
import FormStepper from './FormStepper';
import StepFields from './StepFields';
import StepNavigation from './StepNavigation';
import useStepGroups from './useStepGroups';
import useStepValidation from './useStepValidation';
import useMultiStepSubmit from './useMultiStepSubmit';

type Props = {
  data: DynamicFormType;
};

const MultiStepForm = ({ data }: Props) => {
  const { form } = data;
  const steps = form?.steps ?? [];
  const allFields = form?.fields ?? [];
  const formType = form?.formType ?? 'multiSaveAfter';

  const hideStepper = form?.hideStepper ?? false;
  const stepGroups = useStepGroups({ fields: allFields, steps });
  const totalSteps = stepGroups.length;

  const { currentStep, error, success, goBack, onStepSubmit } =
    useMultiStepSubmit({
      formId: form?.id,
      allFields,
      formType,
      stepGroups,
      totalSteps,
      withRecaptcha: form?.withRecaptcha,
      redirect: form?.redirect,
      metaTracking: form?.metaTracking,
      conditionalRedirects: form?.conditionalRedirects,
    });

  const validationSchema = useStepValidation({
    fields: allFields,
    currentStep: currentStep + 1,
  });
  const initialValues = useInitialValues({ fields: allFields });
  const currentGroup = stepGroups[currentStep];

  return (
    <Formik
      enableReinitialize
      onSubmit={onStepSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <Stack spacing={2} sx={{ color: 'text.primary' }}>
            <Optional condition={!hideStepper}>
              <Box>
                <FormStepper steps={steps} activeStep={currentStep} />
              </Box>
            </Optional>

            <Optional condition={!success}>
              <StepFields fields={currentGroup?.fields ?? []} />
            </Optional>

            <Optional condition={!!error}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Optional>

            <Optional condition={!!success}>
              <RichText>{form?.confirmationMessage}</RichText>
            </Optional>

            <Optional condition={!success}>
              <StepNavigation
                currentStep={currentStep}
                totalSteps={totalSteps}
                formType={formType}
                stepConfig={currentGroup?.config ?? { label: '' }}
                submitButtonLabel={form?.submitButtonLabel}
                isSubmitting={isSubmitting}
                onBack={goBack}
              />
            </Optional>

            {form?.withRecaptcha ? <RecaptchaLinks /> : null}
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default MultiStepForm;
